/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import {
    MessageSquare,
    Database,
    Shield,
    Bot,
    Globe,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    Rocket,
    Settings,
    Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import BrandLogo from "@/components/BrandLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Stepper, StepperItem, StepperTrigger, StepperIndicator } from "@/components/ui/stepper";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Import existing forms
import ChatProviderForm from "@/components/forms/chat/ChatProviderForm";
import EmbeddingSettingsForm from "@/components/forms/embedding/EmbeddingSettingsForm";
import AuthProviderForm from "@/components/forms/auth/AuthProviderForm";
import ConnectionForm from "@/components/forms/agents/ConnectionForm";

// Import mutations and stores
import { useSettingsMutation } from "@/api/mutations/admin/SettingsMutations";
import { useCreateChatProviderMutation } from "@/api/mutations/admin/ChatProviderMutations";
import { useCreateEmbeddingProviderMutation } from "@/api/mutations/admin/EmbeddingProviderMutations";
import { useCreateAuthProviderMutation } from "@/api/mutations/admin/AuthProviderMutations";

import { useUpdateChatProviderForAgentsMutation } from "@/api/mutations/admin/langgraph/AgentMutations";
import { trpc } from "@/lib/trpc";
import { essentialAgents, AgentTypeKey, agentRegistry } from "@shared/config/agent-registry";
import { UseTheme } from "@/components/ThemeProvider";
import { useTranslation } from "react-i18next";

const InitPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mutate: createSettings } = useSettingsMutation("create");
    const { data: agentsResult } = trpc.agents.findMany.useQuery({
        filter: {},
        per_page: 100,
    });
    const agents = useMemo(() => agentsResult?.data || [], [agentsResult?.data]);

    // Provider mutations
    const createChatProviderMutation = useCreateChatProviderMutation();
    const createEmbeddingProviderMutation = useCreateEmbeddingProviderMutation();
    const createAuthProviderMutation = useCreateAuthProviderMutation();
    const updateChatProviderForAgentsMutation = useUpdateChatProviderForAgentsMutation();
    const updateAgentMutation = trpc.agents.update.useMutation({
        onSuccess: () => {
            console.log("Agent enabled successfully");
            toast.success(t("init.agentConfiguredSuccess"));
        },
        onError: (error) => {
            console.error("Failed to enable agent:", error);
            toast.error(t("init.agentConfiguredError"));
        },
    });

    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
    const [agentDialogOpen, setAgentDialogOpen] = useState(false);
    const [skippedSteps, setSkippedSteps] = useState<Set<string>>(new Set());

    // Form data storage
    const [formData, setFormData] = useState({
        chat: {},
        embedding: {},
        authProvider: {},
        agents: {} as Record<string, { configured: boolean; enabled: boolean; data: any; agentId: string }>,
        baseUrl: "",
    });

    const { theme, setTheme } = UseTheme();
    const originalTheme = useRef(theme);

    useEffect(() => {
        // Store the current theme and switch to light for this page
        if (theme !== "light") {
            setTheme("light");
        }

        // Restore the original theme when leaving the page
        return () => {
            setTheme(originalTheme.current);
        };
    }, []);

    // Initialize agents data when agents load
    useEffect(() => {
        if (agents && agents.length > 0) {
            setFormData((prev) => {
                const newAgentsData = agents
                    .filter((agent) => !essentialAgents.includes(agent.type as AgentTypeKey))
                    .reduce(
                        (acc, agent) => {
                            const existingConfig = prev.agents[agent.type];
                            const currentAgentData = existingConfig?.data || {};

                            acc[agent.type] = {
                                configured: existingConfig?.configured || false,
                                enabled: existingConfig?.enabled || false,
                                data: currentAgentData,
                                agentId: agent.id, // Store the agent ID for later use
                            };
                            return acc;
                        },
                        {} as Record<string, { configured: boolean; enabled: boolean; data: any; agentId: string }>
                    );

                // Deep compare the new agents data with the previous one
                if (JSON.stringify(prev.agents) === JSON.stringify(newAgentsData)) {
                    return prev; // No change needed
                }

                return { ...prev, agents: newAgentsData };
            });
        }
    }, [agents]);

    // Form refs
    const formRefs = useRef({
        chat: React.createRef<any>(),
        embedding: React.createRef<any>(),
        authProvider: React.createRef<any>(),
    });

    const steps = [
        {
            id: "welcome",
            title: t("init.welcomeToJunet"),
            subtitle: t("init.initialConfig"),
            icon: Settings,
            color: "from-slate-600 to-slate-700",
            description: t("init.configDescription"),
        },
        {
            id: "chat",
            title: t("init.chatConfiguration"),
            subtitle: t("init.configureAiModel"),
            icon: MessageSquare,
            color: "from-blue-600 to-blue-700",
            description: t("init.selectAiModel"),
        },
        {
            id: "embedding",
            title: t("init.embeddingConfiguration"),
            subtitle: t("init.textEmbeddingSettings"),
            icon: Database,
            color: "from-green-600 to-green-700",
            description: t("init.configureEmbedding"),
        },
        {
            id: "authProvider",
            title: t("init.authConfiguration"),
            subtitle: t("init.msAuthSetup"),
            icon: Shield,
            color: "from-indigo-600 to-indigo-700",
            description: t("init.configureMsAuth"),
        },
        {
            id: "baseUrl",
            title: t("init.appConfiguration"),
            subtitle: t("init.baseUrlSettings"),
            icon: Globe,
            color: "from-purple-600 to-purple-700",
            description: t("init.configureBaseUrl"),
        },
        {
            id: "agents",
            title: t("init.agentConfiguration"),
            subtitle: t("init.configureAgents"),
            icon: Bot,
            color: "from-teal-600 to-teal-700",
            description: t("init.setupAgents"),
        },
        {
            id: "complete",
            title: t("init.complete"),
            subtitle: t("init.allSet"),
            icon: CheckCircle2,
            color: "from-green-600 to-green-700",
            description: t("init.setupComplete"),
        },
    ];

    const getAgentIcon = (reference: string) => {
        const iconMap: Record<string, string> = {
            web: "🔍",
            dalle: "🎨",
            jira: "📋",
            confluence: "📚",
            sql: "🗄️",
            virustotal: "🛡️",
            api: "🔌",
            notion: "📝",
            websites: "🌐",
        };
        return iconMap[reference] || "🤖";
    };

    const getAgentColor = (category: string) => {
        const colorMap: Record<string, string> = {
            Web: "bg-blue-100 text-blue-700 border-blue-200",
            Image: "bg-purple-100 text-purple-700 border-purple-200",
            Management: "bg-green-100 text-green-700 border-green-200",
            Knowledgebase: "bg-orange-100 text-orange-700 border-orange-200",
            Misc: "bg-red-100 text-red-700 border-red-200",
        };
        return colorMap[category] || "bg-gray-100 text-gray-700 border-gray-200";
    };

    // Filter agents to exclude essential agents (router, synthesizer) and create display data
    const agentsList = useMemo(() => {
        if (!agents || agents.length === 0) return [];

        return agents
            .filter((agent) => !essentialAgents.includes(agent.type as AgentTypeKey))
            .map((agent) => {
                const agentMetadata = agentRegistry[agent.type as AgentTypeKey];
                return {
                    id: agent.type, // Use type as ID for consistency
                    name: agent.name,
                    description: agent.description,
                    icon: getAgentIcon(agent.type),
                    color: getAgentColor(agentMetadata?.category || "Misc"),
                    category: agentMetadata?.category || "Misc",
                    status: agent.enabled ? "Active" : "Available",
                    version: "1.0.0",
                    ribbon: null,
                    agentId: agent.id,
                };
            });
    }, [agents]);

    const validateCurrentStep = async (skipValidation = false) => {
        const step = steps[currentStep];

        // If skipping validation, allow progression
        if (skipValidation) {
            return true;
        }

        // Enforce validation for Chat Configuration and Embedding (required steps)
        if (step.id === "chat" || step.id === "embedding") {
            const formRef = formRefs.current[step.id as keyof typeof formRefs.current];

            if (formRef?.current?.validateForm) {
                const isValid = await formRef.current.validateForm();

                if (!isValid) {
                    toast.error(`Please complete all required fields in ${step.title}`);
                    return false;
                }
                return true;
            }
        }

        // Optional validation for authProvider
        if (step.id === "authProvider") {
            const formRef = formRefs.current[step.id as keyof typeof formRefs.current];
            if (formRef?.current?.validateForm) {
                const isValid = await formRef.current.validateForm();
                if (!isValid) {
                    // For optional steps, block progression but allow skip
                    toast.error(`Please complete the required fields or use the Skip button to continue`);
                    return false;
                }
                return true;
            }
        }

        // Validation for baseUrl step
        if (step.id === "baseUrl") {
            if (!formData.baseUrl || !formData.baseUrl.trim()) {
                toast.error("Base URL is required");
                return false;
            }
            try {
                new URL(formData.baseUrl);
                return true;
            } catch {
                toast.error("Please enter a valid URL format");
                return false;
            }
        }

        // Welcome and agents steps don't require validation
        if (step.id === "welcome" || step.id === "agents") {
            return true;
        }

        return true;
    };

    const nextStep = async () => {
        if (isValidating) return;

        setIsValidating(true);
        try {
            const isValid = await validateCurrentStep();
            if (isValid && currentStep < steps.length - 1) {
                setCurrentStep((prev) => prev + 1);
            }
        } finally {
            setIsValidating(false);
        }
    };

    const skipStep = async () => {
        if (isValidating) return;

        setIsValidating(true);
        try {
            // Track the skipped step
            const currentStepId = steps[currentStep].id;
            setSkippedSteps((prev) => new Set(prev).add(currentStepId));

            // Skip validation and move to next step
            if (currentStep < steps.length - 1) {
                setCurrentStep((prev) => prev + 1);
                toast.info(`Skipped ${steps[currentStep].title}`);
            }
        } finally {
            setIsValidating(false);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleAgentClick = (agentId: string) => {
        setSelectedAgent(agentId);
        setAgentDialogOpen(true);
    };

    const handleAgentSave = useCallback(
        (agentId: string) => {
            // Enable the agent when a connection is created
            const agentDbId = formData.agents[agentId]?.agentId;
            if (agentDbId) {
                updateAgentMutation.mutate({
                    id: agentDbId,
                    data: { enabled: true },
                });
            }

            setFormData((prev) => {
                return {
                    ...prev,
                    agents: {
                        ...prev.agents,
                        [agentId]: {
                            configured: true,
                            enabled: true, // If a connection was created, the agent is effectively enabled
                            data: {}, // ConnectionForm handles the actual data
                            agentId: prev.agents[agentId]?.agentId || "", // Preserve the agentId
                        },
                    },
                };
            });
            setAgentDialogOpen(false);
            setSelectedAgent(null);
        },
        [formData.agents, updateAgentMutation]
    );

    // Stable callback functions to prevent infinite rerenders
    const handleChatDataChange = useCallback((data: any) => {
        setFormData((prev) => {
            // Only update if data has actually changed
            if (JSON.stringify(prev.chat) === JSON.stringify(data)) {
                return prev;
            }
            return { ...prev, chat: data };
        });
    }, []);

    const handleEmbeddingDataChange = useCallback((data: any) => {
        setFormData((prev) => {
            // Only update if data has actually changed
            if (JSON.stringify(prev.embedding) === JSON.stringify(data)) {
                return prev;
            }
            return { ...prev, embedding: data };
        });
    }, []);

    const handleAuthProviderDataChange = useCallback((data: any) => {
        setFormData((prev) => {
            // Only update if data has actually changed
            if (JSON.stringify(prev.authProvider) === JSON.stringify(data)) {
                return prev;
            }
            return { ...prev, authProvider: data };
        });
    }, []);

    // Function to create agent connections for configured agents
    // Note: ConnectionForm will create connections directly, but we keep this for completeness
    const saveAgentConfigurations = async () => {
        // Since ConnectionForm handles connection creation directly during configuration,
        // this function may not be needed during the init process.
        // Connections are created when users configure agents through the dialog.
        console.log("Agent connections already created through ConnectionForm");
        return Promise.resolve();
    };

    const handleComplete = async () => {
        setIsCompleting(true);

        try {
            // First create agent connections for configured agents (handled by ConnectionForm)
            await saveAgentConfigurations();

            // Create provider records for each configured provider
            const providerPromises: Promise<void>[] = [];

            // Create chat provider if configured
            let createdChatProviderId: string | null = null;
            if (formData.chat && Object.keys(formData.chat).length > 0) {
                const chatConfig = formData.chat as any;
                const chatProviderData = {
                    provider: chatConfig.CHAT_PROVIDER || "azure",
                    display_name: chatConfig.display_name || "Default Chat Model",
                    enabled: true,
                    config: formData.chat,
                };
                await new Promise<void>((resolve, reject) => {
                    createChatProviderMutation.mutate(chatProviderData, {
                        onSuccess: (data) => {
                            createdChatProviderId = data.id;
                            resolve();
                        },
                        onError: (error) => reject(error),
                    });
                });
            }

            // Create embedding provider if not skipped
            if (formData.embedding && Object.keys(formData.embedding).length > 0) {
                const embeddingConfig = formData.embedding as any;
                const embeddingProviderData = {
                    provider: embeddingConfig.EMBEDDING_PROVIDER,
                    display_name: embeddingConfig.display_name || "Default Embedding Model",
                    enabled: true,
                    config: formData.embedding,
                };
                providerPromises.push(
                    new Promise<void>((resolve, reject) => {
                        createEmbeddingProviderMutation.mutate(embeddingProviderData, {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        });
                    })
                );
            }

            // Create auth provider if configured
            if (!skippedSteps.has("authProvider") && formData.authProvider && Object.keys(formData.authProvider).length > 0) {
                const authStoreConfig = formData.authProvider as any;
                const authProviderData = {
                    provider: authStoreConfig.AUTH_PROVIDER || "microsoft",
                    display_name: authStoreConfig.display_name || "Default Auth Provider",
                    enabled: true,
                    config: formData.authProvider,
                };
                providerPromises.push(
                    new Promise<void>((resolve, reject) => {
                        createAuthProviderMutation.mutate(authProviderData, {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        });
                    })
                );
            }

            // Wait for all provider creations to complete
            if (providerPromises.length > 0) {
                await Promise.all(providerPromises);
            }

            // Update agents' chatProviderId if a chat provider was created
            if (createdChatProviderId) {
                await new Promise<void>((resolve, reject) => {
                    updateChatProviderForAgentsMutation.mutate(
                        { chatProviderId: createdChatProviderId! },
                        {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        }
                    );
                });
            }

            // Then save the main settings (without agents_settings and excluding skipped steps)
            const allSettings = {
                chat_settings: formData.chat,
                embedding_settings: formData.embedding,
                auth_provider_settings: !skippedSteps.has("authProvider") ? formData.authProvider : undefined,
                base_url: formData.baseUrl,
                setup_completed: true,
            };

            createSettings(allSettings);

            toast.success("Workspace configuration completed successfully!");

            // Show success for 3 seconds then redirect
            setTimeout(() => {
                navigate("/");
            }, 3000);
        } catch (error: any) {
            console.error("Setup failed:", error);
            toast.error(error?.message || "Failed to complete workspace setup. Please try again.");
            setIsCompleting(false);
        }
    };

    const renderStepContent = () => {
        const step = steps[currentStep];

        switch (step.id) {
            case "welcome":
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6 text-center"
                    >
                        <div className="mb-4 text-6xl">🎉</div>
                        <p className="mx-auto max-w-md text-lg text-muted-foreground">{step.description}</p>
                        <div className="flex justify-center space-x-4 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-2">
                                <Settings className="h-4 w-4" />
                                <span>Easy Setup</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Zap className="h-4 w-4" />
                                <span>Quick Start</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Rocket className="h-4 w-4" />
                                <span>Ready to Go</span>
                            </div>
                        </div>
                    </motion.div>
                );

            case "chat":
                return (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                        <ChatProviderForm
                            ref={formRefs.current.chat}
                            initialData={formData.chat}
                            onDataChange={handleChatDataChange}
                            hideSubmitButton
                        />
                    </motion.div>
                );

            case "embedding":
                return (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                        <EmbeddingSettingsForm
                            ref={formRefs.current.embedding}
                            initialData={formData.embedding}
                            onDataChange={handleEmbeddingDataChange}
                            hideSubmitButton
                        />
                    </motion.div>
                );

            case "authProvider":
                return (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                        <AuthProviderForm
                            ref={formRefs.current.authProvider}
                            initialData={formData.authProvider}
                            onDataChange={handleAuthProviderDataChange}
                            hideSubmitButton
                        />
                    </motion.div>
                );

            case "agents":
                return (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                        {!agents || agents.length === 0 ? (
                            <div className="flex justify-center py-8">
                                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                    {agentsList.map((agent) => {
                                        const agentStatus = formData.agents[agent.id];
                                        return (
                                            <Card
                                                key={agent.id}
                                                className={cn(
                                                    "relative cursor-pointer overflow-hidden border-2 transition-all duration-200 hover:shadow-md",
                                                    agentStatus?.configured
                                                        ? agentStatus.enabled
                                                            ? "border-green-300 bg-green-50"
                                                            : "border-yellow-300 bg-yellow-50"
                                                        : "border-gray-200 hover:border-gray-300"
                                                )}
                                                onClick={() => handleAgentClick(agent.id)}
                                            >
                                                {/* Ribbon */}
                                                {agent.ribbon && (
                                                    <div
                                                        className={cn(
                                                            "absolute right-0 top-0 z-10 px-2 py-1 text-xs font-semibold shadow-md",
                                                            agent.ribbon === "New"
                                                                ? "bg-primary text-white"
                                                                : "bg-blue-500 text-white"
                                                        )}
                                                        style={{ borderBottomLeftRadius: "6px" }}
                                                    >
                                                        {agent.ribbon}
                                                    </div>
                                                )}

                                                <CardContent className="p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="text-2xl">{agent.icon}</div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-2">
                                                                    <h3 className="text-sm font-semibold">
                                                                        {agent.name}
                                                                    </h3>
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {agent.category}
                                                                    </Badge>
                                                                </div>
                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                    {agent.description}
                                                                </p>
                                                                <div className="mt-2 flex items-center space-x-2">
                                                                    <div
                                                                        className={cn(
                                                                            "h-2 w-2 rounded-full",
                                                                            agent.status === "Active"
                                                                                ? "bg-green-500"
                                                                                : "bg-gray-400"
                                                                        )}
                                                                    />
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {agent.status || "Available"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end space-y-1">
                                                            {agentStatus?.configured && (
                                                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                                <div className="space-y-2 text-center">
                                    <p className="text-sm text-muted-foreground">
                                        {t("init.clickAgentToConfigure")}
                                    </p>
                                    <div className="flex justify-center space-x-4 text-xs text-muted-foreground">
                                        <div className="flex items-center space-x-1">
                                            <div className="h-2 w-2 rounded-full bg-green-500" />
                                            <span>{t("init.configuredEnabled")}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <div className="h-2 w-2 rounded-full bg-yellow-500" />
                                            <span>{t("init.configured")}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <div className="h-2 w-2 rounded-full bg-gray-400" />
                                            <span>{t("init.notConfigured")}</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                );

            case "baseUrl":
                return (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="baseUrl" className="text-sm font-medium">
                                    Base URL
                                </label>
                                <input
                                    id="baseUrl"
                                    type="url"
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
                                    placeholder="https://your-junet-app.com"
                                    value={formData.baseUrl}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, baseUrl: e.target.value }))}
                                />
                            </div>
                            <div className="max-w-[33rem] rounded-lg border border-blue-200 bg-blue-50 p-4">
                                <p className="text-sm text-blue-700">
                                    This URL will be used for webhooks, redirects, and API callbacks. Make sure it's
                                    accessible from the internet if you plan to use external integrations.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                );

            case "complete":
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="space-y-6 text-center"
                    >
                        <motion.div
                            animate={{
                                rotate: 360,
                                scale: [1, 1.1, 1],
                            }}
                            transition={{
                                rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                                scale: { duration: 1, repeat: Infinity, ease: "easeInOut" },
                            }}
                            className="mb-4 text-6xl"
                        >
                            🚀
                        </motion.div>
                        <div className="space-y-2">
                            <h3 className="text-xl font-semibold">Setup Complete!</h3>
                            <p className="text-muted-foreground">
                                Your Junet workspace is ready. Redirecting to chat...
                            </p>
                        </div>
                        <div className="flex justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
                        </div>
                    </motion.div>
                );

            default:
                return null;
        }
    };

    const selectedAgentData = useMemo(() => {
        return selectedAgent ? agentsList.find((a) => a.id === selectedAgent) : null;
    }, [selectedAgent, agentsList]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
            <div className="min-w-[33rem] max-w-[80rem]">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-5 text-center"
                >
                    {/* Use branded logo if available  mb-8 h-8 */}
                    <BrandLogo className="mx-auto mb-8 h-8" alt="Junet Logo" />
                    {/* Stepper */}
                    <div>
                        <div className="flex items-center justify-center">
                            <Badge className="text-xs">
                                Step {currentStep + 1} of {steps.length}
                            </Badge>
                        </div>
                        <div className="mx-auto max-w-2xl">
                            <Stepper value={currentStep + 1} className="gap-1">
                                {steps.map((_, index) => (
                                    <StepperItem key={index} step={index + 1} className="flex-1">
                                        <StepperTrigger className="w-full flex-col items-start gap-2" asChild>
                                            <StepperIndicator asChild className="h-1 w-full bg-border">
                                                <span className="sr-only">Step {index + 1}</span>
                                            </StepperIndicator>
                                        </StepperTrigger>
                                    </StepperItem>
                                ))}
                            </Stepper>
                        </div>
                    </div>
                </motion.div>

                {/* Main Card */}
                <motion.div layout className="relative">
                    <Card className="overflow-hidden">
                        {/* Gradient Header */}
                        {/* <div className={cn("h-2 bg-gradient-to-r", steps[currentStep].color)} /> */}

                        <CardHeader className="pb-4 text-center">
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mb-2 flex items-center justify-center space-x-3"
                            >
                                {React.createElement(steps[currentStep].icon, { className: "w-6 h-6 text-primary" })}
                                <CardTitle className="text-2xl">{steps[currentStep].title}</CardTitle>
                            </motion.div>
                            <CardDescription className="text-base">{steps[currentStep].subtitle}</CardDescription>
                        </CardHeader>

                        <CardContent className="flex min-h-[32rem] flex-col">
                            <div className="flex-1">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentStep}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {renderStepContent()}
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* Navigation */}
                            <div className="mt-6 flex items-center justify-between border-t pt-6">
                                <Button
                                    variant="outline"
                                    onClick={prevStep}
                                    disabled={currentStep === 0 || isValidating || isCompleting}
                                    className="flex items-center space-x-2"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    <span>{t("init.previous")}</span>
                                </Button>

                                <div className="flex items-center space-x-2">
                                    {/* Show skip button for optional steps */}
                                    {steps[currentStep].id === "authProvider" && (
                                        <Button
                                            variant="ghost"
                                            onClick={skipStep}
                                            disabled={isValidating || isCompleting}
                                            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground"
                                        >
                                            <span>Skip</span>
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    )}

                                    {currentStep === steps.length - 1 ? (
                                        <Button
                                            onClick={handleComplete}
                                            disabled={isCompleting}
                                            className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                                        >
                                            {isCompleting ? (
                                                <>
                                                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                                    <span>Completing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>Launch Junet</span>
                                                    <Rocket className="h-4 w-4" />
                                                </>
                                            )}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={nextStep}
                                            disabled={isValidating}
                                            className="flex items-center space-x-2"
                                        >
                                            {isValidating ? (
                                                <>
                                                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                                    <span>{t("init.validating")}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span>{t("init.next")}</span>
                                                    <ArrowRight className="h-4 w-4" />
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Agent Configuration Dialog */}
                <Dialog open={agentDialogOpen} onOpenChange={setAgentDialogOpen}>
                    <DialogContent className="max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-6xl">
                        <DialogHeader>
                            <DialogTitle className="flex items-center space-x-2">
                                <span className="text-2xl">{selectedAgentData?.icon}</span>
                                <span>{t("init.configureAgent", { name: selectedAgentData?.name })}</span>
                            </DialogTitle>
                        </DialogHeader>

                        {selectedAgentData && selectedAgent && (
                            <ConnectionForm
                                agentId={formData.agents[selectedAgent]?.agentId || ""}
                                agentType={selectedAgent as AgentTypeKey}
                                onSuccess={() => {
                                    handleAgentSave(selectedAgent);
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default InitPage;
